import { useQuery } from '@tanstack/react-query';
import {
  getActivityDetails,
  type ActivityDetailParams,
} from '@/lib/api/user-activity-details';
import type { ActivityInterval, ActivityScope } from '@/lib/api/user-activity';

export const activityDetailKeys = {
  all: ['activity-details'] as const,
  detail: (
    metricType: string,
    interval: ActivityInterval,
    page: number,
    scope: ActivityScope,
    timeZone: string,
    scopeId?: string,
    startDate?: string,
    endDate?: string
  ) =>
    [
      ...activityDetailKeys.all,
      metricType,
      interval,
      page,
      scope,
      timeZone,
      scopeId,
      startDate,
      endDate,
    ] as const,
};

export interface UseActivityDetailsParams {
  metricType: ActivityDetailParams['metricType'];
  interval: ActivityInterval;
  page: number;
  scope: ActivityScope;
  scopeId?: string;
  timeZone: string;
  startDate?: string;
  endDate?: string;
  enabled: boolean;
  limit?: number;
  cursor?: string;
}

export function useActivityDetails(params: UseActivityDetailsParams) {
  const {
    metricType,
    interval,
    page,
    scope,
    scopeId,
    timeZone,
    startDate,
    endDate,
    enabled,
    limit,
    cursor,
  } = params;

  return useQuery({
    queryKey: activityDetailKeys.detail(
      metricType,
      interval,
      page,
      scope,
      timeZone,
      scopeId,
      startDate,
      endDate
    ),
    queryFn: () => {
      const apiParams: ActivityDetailParams = {
        metricType,
        interval,
        page,
        scope,
        timeZone,
      };
      if (scopeId !== undefined) apiParams.scopeId = scopeId;
      if (startDate !== undefined) apiParams.startDate = startDate;
      if (endDate !== undefined) apiParams.endDate = endDate;
      if (limit !== undefined) apiParams.limit = limit;
      if (cursor !== undefined) apiParams.cursor = cursor;
      return getActivityDetails(apiParams);
    },
    enabled,
    staleTime: 30_000, // 30 seconds, matches dashboard
    refetchOnWindowFocus: false, // user explicitly opened the drill-down
  });
}
